// Review Cockpit (MUI, /cockpit). Presentation port of CockpitPage — same
// data/hooks (review queue, ledger, decisions). Rebuilt with mui/* primitives +
// brainTokens under MuiAppShell. One-column, touch-friendly layout.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { reviewPending, approveMemory, rejectMemory } from "../../lib/brain-api";
import { getApiUrl, getIdToken } from "../../lib/auth";
import type { PendingItem } from "../../lib/brain-types";
import { brainTokens } from "../../design/brainTheme";
import { SectionCard, EmptyState, Icon } from "../../mui";

const c = brainTokens.color;

interface LedgerUnit {
  source: string;
  unit: string;
  status: string;
  note?: string;
  atomCount?: number;
}
interface DecisionRow {
  slug: string;
  status: "open" | "resolved";
  question: string;
  answer?: string;
}

async function fetchLedger(): Promise<{ units: LedgerUnit[]; decisions: DecisionRow[] }> {
  const token = await getIdToken();
  const res = await fetch(`${getApiUrl()}/campaign/ledger`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`ledger ${res.status}`);
  return res.json();
}
async function resolveDecision(slug: string, answer: string): Promise<void> {
  const token = await getIdToken();
  const res = await fetch(`${getApiUrl()}/campaign/decision`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ slug, ...(answer.trim() && { answer: answer.trim() }) }),
  });
  if (!res.ok) throw new Error(`resolve ${res.status}`);
}

function DecisionCard({ d, onDone }: { d: DecisionRow; onDone: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const onResolve = async () => {
    setBusy(true);
    try {
      await resolveDecision(d.slug, answer);
      enqueueSnackbar(`Resolved ${d.slug}.`, { variant: "success" });
      onDone();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Could not resolve.", { variant: "error" });
      setBusy(false);
    }
  };
  const urgent = /SECURITY/i.test(d.question);
  return (
    <Box
      sx={{
        borderTop: `1px solid ${c.border}`,
        pt: "10px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {urgent && <Icon name="warning" size={16} stroke={1.8} style={{ color: c.dangerText }} />}
        <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.muted2 }}>
          {d.slug}
        </Box>
      </Box>
      <Box sx={{ fontSize: 13, color: c.ink }}>{d.question}</Box>
      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Answer / note (optional)"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Button variant="contained" size="small" disabled={busy} onClick={() => void onResolve()}>
          Resolve
        </Button>
      </Box>
    </Box>
  );
}

function FactRow({
  item,
  busy,
  onApprove,
  onReject,
}: {
  item: PendingItem;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const [open, setOpen] = useState(false);
  const firstLine = item.text.split(/[\n.]/, 1)[0];
  return (
    <Box
      sx={{
        display: "flex",
        gap: "10px",
        alignItems: "flex-start",
        borderTop: `1px solid ${c.border}`,
        pt: "8px",
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Box
        component="button"
        onClick={() => setOpen(!open)}
        title={open ? "Collapse" : "Expand"}
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: "left",
          background: "none",
          border: "none",
          p: 0,
          cursor: "pointer",
          fontSize: 13,
          lineHeight: 1.5,
          color: c.muted,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "inherit",
          "&:hover": { color: c.ink },
        }}
      >
        {open ? item.text : firstLine + (item.text.length > firstLine.length ? "…" : "")}
      </Box>
      <Box sx={{ display: "flex", gap: "6px", flexShrink: 0, alignSelf: { xs: "flex-end", sm: "flex-start" } }}>
        <Button
          variant="outlined"
          size="small"
          color="error"
          disabled={busy}
          onClick={onReject}
          sx={{ borderColor: c.dangerBorder }}
        >
          Reject
        </Button>
        <Button variant="contained" size="small" disabled={busy} onClick={onApprove}>
          Approve
        </Button>
      </Box>
    </Box>
  );
}

export function CockpitPageMui() {
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState<PendingItem[] | null>(null);
  const [ledger, setLedger] = useState<{ units: LedgerUnit[]; decisions: DecisionRow[] } | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [groupBusy, setGroupBusy] = useState<string | null>(null);

  const reload = useCallback(() => {
    reviewPending(undefined, 250)
      .then(setPending)
      .catch(() => setPending([]));
    fetchLedger()
      .then(setLedger)
      .catch(() => setLedger({ units: [], decisions: [] }));
  }, []);
  useEffect(() => reload(), [reload]);

  const groups = useMemo(() => {
    const map = new Map<string, PendingItem[]>();
    for (const p of pending ?? []) {
      const key = p.customer?.[0] ?? (p.target_scope.startsWith("dept:") ? p.target_scope : "ungrouped");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [pending]);

  const act = async (item: PendingItem, action: "approve" | "reject") => {
    setBusyIds((s) => new Set(s).add(item.item_id));
    try {
      if (action === "approve") await approveMemory(item.item_id, item.target_scope);
      else await rejectMemory(item.item_id, item.target_scope);
      setPending((p) => (p ?? []).filter((x) => x.item_id !== item.item_id));
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : `Could not ${action}.`, { variant: "error" });
    } finally {
      setBusyIds((s) => {
        const n = new Set(s);
        n.delete(item.item_id);
        return n;
      });
    }
  };

  const approveGroup = async (key: string, items: PendingItem[]) => {
    setGroupBusy(key);
    let ok = 0;
    for (const item of items) {
      try {
        await approveMemory(item.item_id, item.target_scope);
        ok++;
        setPending((p) => (p ?? []).filter((x) => x.item_id !== item.item_id));
      } catch {
        enqueueSnackbar(`Failed on ${item.item_id} — stopped; ${ok}/${items.length} approved.`, { variant: "error" });
        break;
      }
    }
    if (ok === items.length) enqueueSnackbar(`Approved all ${ok} for ${key}.`, { variant: "success" });
    setGroupBusy(null);
  };

  const openDecisions = (ledger?.decisions ?? []).filter((d) => d.status === "open");
  const sources = useMemo(() => {
    const by = new Map<string, { total: number; done: number; atoms: number }>();
    for (const u of ledger?.units ?? []) {
      const s = by.get(u.source) ?? { total: 0, done: 0, atoms: 0 };
      s.total++;
      if (u.status === "extracted" || u.status === "reviewed") s.done++;
      s.atoms += u.atomCount ?? 0;
      by.set(u.source, s);
    }
    return [...by.entries()];
  }, [ledger]);

  const eyebrow =
    pending != null
      ? `${pending.length} facts awaiting review${openDecisions.length ? ` · ${openDecisions.length} decisions` : ""}`
      : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: 860 }}>
      <Box>
        <Box sx={{ fontSize: 36, fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1 }}>Review Cockpit</Box>
        {eyebrow && <Box sx={{ fontSize: 14, color: c.muted2, mt: "10px" }}>{eyebrow}</Box>}
      </Box>

      {pending === null && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", color: c.muted2, fontSize: 13 }}>
          <CircularProgress size={22} sx={{ color: c.teal }} />
          Loading the review queue…
        </Box>
      )}

      {sources.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {sources.map(([source, s]) => (
            <Box
              key={source}
              component="span"
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 12,
                lineHeight: 1.6,
                color: c.muted,
                bgcolor: c.panelBg2,
                border: `1px solid ${c.border}`,
                borderRadius: "999px",
                px: "10px",
                py: "2px",
              }}
            >
              <Box component="strong" sx={{ color: c.ink, fontWeight: 600 }}>
                {source}
              </Box>{" "}
              {s.done}/{s.total} mined{s.atoms ? ` · ${s.atoms} atoms` : ""}
            </Box>
          ))}
        </Box>
      )}

      {openDecisions.length > 0 && (
        <SectionCard title="Decisions for you" headerMb={10}>
          {openDecisions.map((d) => (
            <DecisionCard key={d.slug} d={d} onDone={reload} />
          ))}
        </SectionCard>
      )}

      {pending !== null && pending.length === 0 ? (
        <EmptyState title="Review queue is clear">Every mined fact has been reviewed. 🎉</EmptyState>
      ) : (
        groups.map(([key, items]) => (
          <SectionCard
            key={key}
            title={
              <>
                {key}{" "}
                <Box component="span" sx={{ color: c.muted2, fontWeight: 400 }}>
                  ({items.length})
                </Box>
              </>
            }
            actions={
              <Button size="small" variant="contained" disabled={groupBusy !== null} onClick={() => void approveGroup(key, items)}>
                {groupBusy === key ? "Approving…" : `Approve all ${items.length}`}
              </Button>
            }
            headerMb={10}
          >
            {items.map((item) => (
              <FactRow
                key={item.item_id}
                item={item}
                busy={busyIds.has(item.item_id) || groupBusy === key}
                onApprove={() => void act(item, "approve")}
                onReject={() => void act(item, "reject")}
              />
            ))}
          </SectionCard>
        ))
      )}
    </Box>
  );
}
